
import Vue from 'vue';
import { ListModel as DexListModel, CourseModel } from '@descomplica/dex-models';
import { WeekModel } from '@/models/studyPlanning/WeekModel';
import { ListModel as StudyPlanningListModel } from '@/models/studyPlanning/ListModel';
import { SHORT_MONTHS } from '@/helpers/date';
import WeeklyListsSkeleton from '@/components/skeletons/WeeklyListsSkeleton.vue';
import SectionCards from './SectionCards.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import AnticipateWeeklyListsCard from '@/components/studyPlanning/postgrad/AnticipateWeeklyListsCard.vue';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';

interface ExpandedState {
  [key: string]: boolean;
}

interface Data {
  loadingLists: boolean;
  expandedState: ExpandedState;
}

type WeeklyList = DexListModel & { isOverdue: boolean };

type WeekAndLists = { week: WeekModel; lists: WeeklyList[] };

type WeeklyAndOverdueLists = { currentWeekLists: WeeklyList[]; overdueLists: WeeklyList[] };

export default Vue.extend({
  components: {
    WeeklyListsSkeleton,
    SectionCards,
    AnticipateWeeklyListsCard,
  },
  props: {
    showStudyPlanningSkeleton: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      loadingLists: true,
      expandedState: {},
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    hasWeekToAnticipate(): boolean {
      const studyPlanning = this.$store.getters.studyPlanning(this.currentCourse.id);

      if (!studyPlanning) {
        return false;
      }

      return studyPlanning.hasWeekToAnticipate;
    },
    studyPlanningWeek(): WeekModel {
      return this.$store.getters.studyPlanningWeek;
    },
    studyPlanningAnticipatedWeeks(): WeekModel[] {
      return this.$store.getters.studyPlanningAnticipatedWeeks(
        this.studyPlanningWeek.studyPlanningId,
      );
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    anticipatedLists(): WeekAndLists[] {
      const anticipatedWeeks = this.studyPlanningAnticipatedWeeks;

      const anticipatedStudyPlanningLists = anticipatedWeeks
        .reduce((weeksAndLists: WeekAndLists[], week: WeekModel) => {
          const rawLists = this.$store.getters.studyPlanningAnticipatedWeekListsByWeekId(week.id);
          if (!rawLists) return weeksAndLists;

          const lists = this.reduceAndSortLists(rawLists);

          weeksAndLists.push({
            week,
            lists,
          });

          return weeksAndLists;
        }, [])
        .sort((a: WeekAndLists, b: WeekAndLists): number => {
          if (a.week.position > b.week.position) {
            return -1;
          }

          return 1;
        });

      return anticipatedStudyPlanningLists;
    },
    weeklyAndOverdueLists(): WeeklyAndOverdueLists {
      const { studyPlanningWeekLists } = this.$store.getters;
      if (!studyPlanningWeekLists) {
        return { currentWeekLists: [], overdueLists: [] };
      }

      const currentAndOverdueLists = studyPlanningWeekLists.reduce(
        (lists: any, studyPlanningList: StudyPlanningListModel) => {
          if (studyPlanningList.isOverdue) {
            lists.overdueLists.push(studyPlanningList);

            return lists;
          }

          lists.currentWeekLists.push(studyPlanningList);

          return lists;
        },
        { currentWeekLists: [], overdueLists: [] },
      );

      currentAndOverdueLists.currentWeekLists = this.reduceAndSortLists(
        currentAndOverdueLists.currentWeekLists,
      );
      currentAndOverdueLists.overdueLists = this.reduceAndSortLists(
        currentAndOverdueLists.overdueLists,
      );

      return currentAndOverdueLists;
    },
    arePastWeeksListsFinished(): boolean {
      return (
        !this.weeklyAndOverdueLists.overdueLists.length ||
        this.weeklyAndOverdueLists.overdueLists.every((l) => l.progress === 100)
      );
    },
    weeklyListsProgresses(): number[] {
      const weeklyAndOverdueLists = [
        ...this.weeklyAndOverdueLists.currentWeekLists,
        ...this.weeklyAndOverdueLists.overdueLists,
      ];

      return weeklyAndOverdueLists
        .flat()
        .map((weeklyList: WeeklyList) => Number(weeklyList.progress));
    },
    anticipatedListsProgresses(): number[] {
      const allLists = this.anticipatedLists.reduce(
        (lists: WeeklyList[], weekAndLists: WeekAndLists) => {
          lists.push(...weekAndLists.lists);

          return lists;
        },
        [],
      );

      return allLists.map((weeklyList: WeeklyList) => Number(weeklyList.progress));
    },
    areAllListsCompleted(): boolean {
      this.buildExpandedState();

      return (
        this.weeklyListsProgresses.every((progress) => progress === 100) &&
        this.anticipatedListsProgresses.every((progress) => progress === 100)
      );
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  created() {
    this.$emit('refreshWeeklyLists', this.removeSkeleton);
  },
  methods: {
    shouldShowToggleButton() {
      return Object.keys(this.expandedState).length > 1;
    },
    buildExpandedState() {
      const anticipatedWeeks = this.studyPlanningAnticipatedWeeks.reduce(
        (expandedState: any, week: WeekModel) => {
          expandedState[week.position] = false;

          return expandedState;
        },
        {},
      );

      const currentWeek: any = {};
      currentWeek[this.studyPlanningWeek.position] = false;

      const expandedState: any = { ...anticipatedWeeks, ...currentWeek };
      this.expandedState = expandedState;

      const keys = Object.keys(this.expandedState);
      const biggestPosition: any = keys[keys.length - 1];
      expandedState[biggestPosition] = true;
    },
    toggleSection(position: number) {
      if (this.expandedState[position]) {
        this.expandedState[position] = false;

        return;
      }

      Object.keys(this.expandedState).forEach((stateKey: any) => {
        this.expandedState[stateKey] = false;
      });

      this.expandedState[position] = true;
    },
    formatWeekDate(week: WeekModel): string {
      const startDate = {
        month: SHORT_MONTHS[week.startDate.getMonth()],
        date: week.startDate.getDate(),
      };

      const endDate = {
        month: SHORT_MONTHS[week.endDate.getMonth()],
        date: week.endDate.getDate(),
      };

      return `${startDate.date} ${startDate.month} - ${endDate.date} ${endDate.month}`;
    },
    reduceAndSortLists(lists: StudyPlanningListModel[]): WeeklyList[] {
      return lists
        .reduce((weeklyLists: WeeklyList[], studyPlanningList: StudyPlanningListModel) => {
          const listInfo = this.$store.getters.listById(studyPlanningList.listId);
          if (!listInfo) return weeklyLists;

          weeklyLists.push({
            ...listInfo,
            progress: listInfo.progress,
            classroomId: studyPlanningList.classroomId,
            isOverdue: studyPlanningList.isOverdue,
            isAnticipated: studyPlanningList.isAnticipated,
          });

          return weeklyLists;
        }, [])
        .sort((a: WeeklyList, b: WeeklyList): number => {
          if (a.progress === 100 && b.progress !== 100) {
            return 1;
          }
          if (a.progress !== 100 && b.progress === 100) {
            return -1;
          }

          return 0;
        });
    },
    removeSkeleton() {
      this.loadingLists = false;
    },
    trackListCardClick(
      isFirstOfTheWeek: boolean,
      isOverdue: boolean,
      isAnticipated: boolean,
    ): void {
      Analytics.trackEvent(EVENT_LABELS.lessonCardClicked, {
        course: this.currentCourse.name,
        postgrad_study_planning: true,
        is_first_lesson_of_the_week: isFirstOfTheWeek,
        is_overdue_list: isOverdue,
        is_anticipated_list: isAnticipated,
        user_has_overdue_lists: Boolean(this.weeklyAndOverdueLists.overdueLists.length),
        ...this.getTrialStatusByDescomplicaCourseId,
        ...this.earlyGraduationTrackingProps,
      });
    },
  },
});
